import { useMemo } from "react";
import {
  productScopeSegmentToCode,
  PRODUCT_MAPPING,
  ProductType,
} from "../../openapi-typescript/common/product_types";
import { FeatureFlag, useFeatureFlags } from "../customHoooks/useFeatureFlags";
import { Company } from "../customHoooks/useUpdatableCompanies";
import {
  useAllProductTypes,
  UseAllProductTypesParams,
} from "../customHoooks/useProductTypes";

const normalize = (str: string): string => str.replace(/\s/g, "").toLowerCase();
const getKeyFromValue = (value?: string): string | undefined => {
  return Object.keys(PRODUCT_MAPPING).find(
    (key) => value && normalize(key) === normalize(value)
  );
};

export const getMappedProductTypeFromScopes = (features: FeatureFlag[]) => {
  const productInScope = features
    .map(
      (feature) => feature.match(/^core\.product\.([a-zA-Z0-9-]+)\.read$/)?.[1]
    )
    .filter(Boolean);

  const mappedCodesInScope = productInScope
    .map((product) => productScopeSegmentToCode[`${product}`])
    .filter((value): value is string => Boolean(value));

  return mappedCodesInScope as ProductType["code"][];
};

export const getProductTypeFromScopes = (
  features: FeatureFlag[]
): ProductType["code"][] =>
  features
    .map(
      (feature) => feature.match(/^core\.product\.([a-zA-Z0-9-]+)\.read$/)?.[1]
    )
    .filter(Boolean)
    .map((feature) => getKeyFromValue(feature))
    .filter(Boolean) as ProductType["code"][];

export const useProductTypeByScope = ({
  companyId,
  additionalParams,
}: {
  companyId?: Company["id"];
  additionalParams?: UseAllProductTypesParams;
}) => {
  const { features } = useFeatureFlags(companyId);

  const allCodes = useMemo(() => {
    const availableCodes = getProductTypeFromScopes(features);
    const mappedCodes = getMappedProductTypeFromScopes(features);

    return [...availableCodes, ...mappedCodes];
  }, [features]);

  const { updatable, loading, sync, error } = useAllProductTypes({
    ...additionalParams,
    code: allCodes,
  });

  return {
    updatable,
    loading,
    sync,
    error,
  };
};
